var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useCombobox, useMultipleSelection } from 'downshift';
import { FloatingContextProvider } from '../utils/floating/index.js';
import { MultiselectSelectedItem } from './MultiselectButton.js';
import { MultiselectContext } from './MultiselectContext.js';
import { MultiselectCreatableButton } from './MultiselectCreatableButton.js';
import { MultiselectCreatableItems } from './MultiselectCreatableItems.js';
import { MultiselectItem } from './MultiselectItem.js';
import { MultiselectItemsEmpty } from './MultiselectItems.js';
export const MultiselectCreatable = (_a) => {
    var { children, value = [], options, onChange, className, disabled = false, searchable = false, onInputChange, onCreateItem, createItemPosition = 'last', placement = 'bottom-start' } = _a, rest = __rest(_a, ["children", "value", "options", "onChange", "className", "disabled", "searchable", "onInputChange", "onCreateItem", "createItemPosition", "placement"]);
    const [creating, setCreating] = useState(false);
    const [inputItems, setInputItems] = useState(options);
    const dsMultiple = useMultipleSelection({
        selectedItems: value,
        onSelectedItemsChange: changes => {
            if (typeof onChange === 'function') {
                onChange(changes.selectedItems);
            }
        },
        stateReducer: (_, actionAndChanges) => {
            const { type, changes } = actionAndChanges;
            switch (type) {
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                    return Object.assign(Object.assign({}, changes), { activeIndex: null });
                default:
                    return changes;
            }
        },
    });
    const dsCombobox = useCombobox({
        items: inputItems,
        selectedItem: null,
        onSelectedItemChange: changes => {
            if (!changes.selectedItem ||
                changes.type === useCombobox.stateChangeTypes.InputBlur) {
                return;
            }
            const index = dsMultiple.selectedItems.indexOf(changes.selectedItem);
            if (index > 0) {
                dsMultiple.setSelectedItems([
                    ...dsMultiple.selectedItems.slice(0, index),
                    ...dsMultiple.selectedItems.slice(index + 1),
                ]);
            }
            else if (index === 0) {
                dsMultiple.setSelectedItems([...dsMultiple.selectedItems.slice(1)]);
            }
            else {
                dsMultiple.setSelectedItems([
                    ...dsMultiple.selectedItems,
                    changes.selectedItem,
                ]);
            }
        },
        stateReducer: (state, actionAndChanges) => {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return Object.assign(Object.assign({}, changes), { isOpen: true, highlightedIndex: state.highlightedIndex, inputValue: '' });
                case useCombobox.stateChangeTypes.InputBlur:
                    return Object.assign(Object.assign({}, changes), { inputValue: '' });
                default:
                    return changes;
            }
        },
        onInputValueChange: changes => {
            setCreating(false);
            setInputItems([]);
            if (searchable) {
                onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(changes.inputValue);
            }
        },
        onStateChange: changes => {
            const { type, selectedItem } = changes;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (onCreateItem && creating && inputValue === selectedItem) {
                        onCreateItem(inputValue);
                        setCreating(false);
                    }
                    break;
                default:
                    break;
            }
        },
    });
    const { inputValue, setHighlightedIndex } = dsCombobox;
    useEffect(() => {
        if (inputValue.length > 0) {
            if (!options.includes(inputValue)) {
                setCreating(true);
                if (createItemPosition === 'last') {
                    setInputItems([...options, inputValue]);
                }
                else {
                    setInputItems([inputValue, ...options]);
                }
                setHighlightedIndex(0);
            }
            else {
                setInputItems(options);
                setCreating(false);
            }
        }
        else {
            setInputItems(options);
        }
    }, [options, inputValue, createItemPosition, setHighlightedIndex]);
    return (React.createElement("div", Object.assign({ className: clsx('relative isolate', className) }, rest),
        React.createElement(MultiselectContext.Provider, { value: Object.assign(Object.assign(Object.assign({}, dsMultiple), dsCombobox), { searchable,
                disabled,
                creating,
                onCreateItem }) },
            React.createElement(FloatingContextProvider, { placement: placement }, children))));
};
MultiselectCreatable.Button = MultiselectCreatableButton;
MultiselectCreatable.SelectedItem = MultiselectSelectedItem;
MultiselectCreatable.Items = MultiselectCreatableItems;
MultiselectCreatable.ItemsEmpty = MultiselectItemsEmpty;
MultiselectCreatable.Item = MultiselectItem;
//# sourceMappingURL=MultiselectCreatable.js.map